import React from 'react';
import { Divider, Tag } from 'antd';
import { Timer, PhoneCall, Wallet } from 'lucide-react';

interface PricingDetailsTabProps {
	duration?: string;
	transferredCallDuration?: number;
	costCoveredByFreeMinutes?: number;
	aiCallCost?: number;
	transferredCallCost?: number;
}

const PricingDetailsTab: React.FC<PricingDetailsTabProps> = ({
	duration,
	transferredCallDuration,
	costCoveredByFreeMinutes,
	aiCallCost,
	transferredCallCost,
}) => {
	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = Math.round(seconds % 60);
		return `${minutes}m ${remainingSeconds}s`;
	};

	// Calculate durations
	const totalDuration = duration ? parseInt(duration) + (transferredCallDuration || 0) : 0;

	// Calculate costs
	const freeCost = Number(costCoveredByFreeMinutes) || 0;
	const aiCost = Number(aiCallCost) || 0;
	const transferCost = Number(transferredCallCost) || 0;
	const totalCost = Number(aiCost + transferCost + freeCost);
	const finalChargedAmount = Number(totalCost - freeCost);

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-2 mb-6">
				<Wallet className="w-6 h-6" />
				<h3 className="text-xl font-semibold">Call Pricing Details</h3>
			</div>

			<div>
				<div className="flex items-center gap-2 mb-4">
					<Timer className="w-5 h-5" />
					<h3 className="text-base font-medium">Duration Details</h3>
				</div>

				<div className="grid grid-cols-2 gap-4 mb-4">
					{duration && (
						<div className="bg-gray-50 p-3 rounded">
							<div className="text-sm text-gray-600 mb-1">AI Reception</div>
							<div className="text-lg font-medium">{formatTime(parseInt(duration))}</div>
						</div>
					)}
					<div className="bg-gray-50 p-3 rounded">
						<div className="text-sm text-gray-600 mb-1">Call Transfer</div>
						<div className="text-lg font-medium">{formatTime(transferredCallDuration || 0)}</div>
					</div>
				</div>

				<div className="flex justify-between items-center pt-3 border-t border-gray-100">
					<span className="text-gray-600">Total Duration</span>
					<span className="font-medium">{formatTime(totalDuration)}</span>
				</div>
			</div>

			<div>
				<div className="flex items-center gap-2 mb-4">
					<PhoneCall className="w-5 h-5" />
					<h3 className="text-base font-medium">Cost Details</h3>
				</div>

				{!!freeCost && (
					<div className="bg-blue-50 p-3 rounded mb-3">
						<div className="flex justify-between items-center">
							<div className="flex items-center gap-2">
								<span>Free Minutes</span>
								<Tag className="m-0" color="success">
									FREE
								</Tag>
							</div>
							<span className="text-green-600 font-medium">${freeCost.toFixed(4)}</span>
						</div>
					</div>
				)}

				{!!aiCost && (
					<div className="bg-gray-50 p-3 rounded mb-3">
						<div className="flex justify-between items-center">
							<span className="text-gray-600">AI Receptionist Call Cost</span>
							<span className="font-medium">${aiCost.toFixed(4)}</span>
						</div>
					</div>
				)}

				<div className="bg-gray-50 p-3 rounded mb-3">
					<div className="flex justify-between items-center">
						<span className="text-gray-600">Transfer Call Cost</span>
						<span className="font-medium">${transferCost.toFixed(4)}</span>
					</div>
				</div>

				<Divider className="my-3" />
				<div className="flex justify-between items-center mb-3 p-3">
					<span className="font-medium">Total Cost</span>
					<span className="font-medium">${totalCost.toFixed(4)}</span>
				</div>

				<div className="bg-gray-100 p-3 rounded">
					<div className="flex justify-between items-center">
						<div>
							<span className="font-medium">Amount Charged</span>
							<div className="text-sm text-gray-500">(Total Cost - Free Minutes)</div>
						</div>
						<span className="text-lg text-blue-600 font-bold">
							${finalChargedAmount.toFixed(4)}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PricingDetailsTab;
